import { KeyboardEvent, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RouterLink } from '@/common/components/RouterLink/RouterLink';
import { MONTHS } from '@/common/constants/date';
import { ROUTES } from '@/common/constants/routes';
import { AnalyticsService } from '@/common/integrations/analytics/analytics.service';
import { useGetEmployeeTaxProfile } from '@/common/integrations/menhir/employee/employee/employee.hooks';
import { useGetSavingsDetails } from '@/common/integrations/menhir/employee/savings/savings.hooks';
import { now, parseDate } from '@/common/utils/date';
import { InfoButton } from '@/web/components/InfoButton/InfoButton';
import { NestedRoute } from '@/web/components/NestedRoute';
import PlaceholderCard from '@/web/components/PlaceholderCard';
import { usePageViewTracking } from '@/web/hooks/usePageViewTracking';
import {
  Button,
  Card,
  Flex,
  FloatingSelectField,
  Grid,
  IconCoverflex,
  InlineLoading,
  PageHeader,
  themeSprinkles,
} from '@coverflex-tech/hypernova';
import { MoneyUtils } from '@coverflex-tech/hypernova-utils';
import classNames from 'classnames';
import { differenceInMonths, sub } from 'date-fns';
import { Redirect } from 'expo-router';
import Accordion from './components/Accordion';
import SavingsLearningCentre from './components/LearningCentre';
import css from './savings-page.module.css';

type MonthYear = { month?: number; year?: number };

const getPeriods = (lastPeriod: MonthYear): MonthYear[] => {
  if (!lastPeriod.month || !lastPeriod.year) return [];
  const currentDate = now();
  const startDate = new Date(lastPeriod.year, lastPeriod.month - 1);
  const diff = differenceInMonths(currentDate, startDate);
  return Array.from(new Array(diff + 1)).map((_val: number, idx: number) => {
    const current = sub(currentDate, { months: idx });
    return {
      month: current.getMonth() + 1,
      year: current.getFullYear(),
    };
  });
};

export const SavingsPage = () => {
  const [showLearningCentre, setShowLearningCentre] = useState(false);
  const [pristine, setPristine] = useState(true);
  const { t } = useTranslation();
  const [monthYear, setMonthYear] = useState<MonthYear>({
    month: undefined,
    year: undefined,
  });

  usePageViewTracking('savings_details_page');

  const { savingsDetails, error, isLoading } = useGetSavingsDetails(monthYear);
  const { taxProfile, error: taxError } = useGetEmployeeTaxProfile();

  const monthYearList = useMemo(() => {
    if (savingsDetails?.firstMovementDate) {
      const date = parseDate(savingsDetails.firstMovementDate);
      const periods = getPeriods({
        month: date.getMonth() + 1,
        year: date.getFullYear(),
      });
      return [{ month: undefined, year: undefined } as MonthYear, ...periods];
    }
    return [];
  }, [savingsDetails?.firstMovementDate]);

  const hasTaxProfile = !!taxProfile && !taxError;

  if (error) {
    return <Redirect href={ROUTES.BENEFITS} />;
  }

  const handleKeyDown = ({ key }: KeyboardEvent) => {
    if (key === 'Tab') setPristine(false);
    if (key === 'Escape') setPristine(true);
  };

  const cardTitle = monthYear.month
    ? t('dashboard.savings.card.title-month', {
        month: t(MONTHS[monthYear.month - 1] ?? ''),
      })
    : t('dashboard.savings.card.title');

  const savingsPlaceholderTitle = monthYear.month
    ? t('dashboard.savings.placeholder.title-no-savings-month')
    : t('dashboard.savings.placeholder.title-no-savings');

  return (
    <div onKeyDown={handleKeyDown}>
      <NestedRoute backRoute={ROUTES.BENEFITS}>
        {showLearningCentre && (
          <SavingsLearningCentre onClose={() => setShowLearningCentre(false)} />
        )}
        <PageHeader
          title={t('dashboard.savings.title')}
          subtitle={t('dashboard.savings.subtitle')}
          rightHeader={
            <InfoButton
              onClick={() => {
                AnalyticsService.trackCustomEvent(
                  'learning_centre_savings_button_clicked'
                );
                setShowLearningCentre(true);
              }}
            />
          }
        />
        {!hasTaxProfile && (
          <span className={css.taxProfile}>
            {t('dashboard.savings.create_tax_profile_question')}

            <RouterLink
              href={ROUTES.PERSONAL_DETAILS}
              className={classNames(css.link, {
                [css.pristine!]: pristine,
              })}
              onPress={() =>
                AnalyticsService.trackCustomEvent(
                  'create_fiscal_profile_button_clicked',
                  { context: 'savings_details_page' }
                )
              }
            >
              <Button
                variant="tertiary"
                size="sm"
                presentational
                className={css.taxProfileLink}
              >
                {t('dashboard.savings.create_tax_profile')}
              </Button>
            </RouterLink>
          </span>
        )}
        <Grid.Root className={themeSprinkles({ marginTop: 'md' })}>
          <Grid.Column span={6}>
            <Flex flexDirection="column" gap="md">
              <FloatingSelectField
                id="savings-select"
                label={t('dashboard.savings.select_period')}
                value={monthYear}
                options={monthYearList}
                onChange={(_id, value) => value && setMonthYear(value)}
                itemToString={(item) =>
                  item?.month
                    ? `${t(MONTHS[item.month - 1] ?? '')} ${item.year}`
                    : t('dashboard.savings.since-forever')
                }
              />
              <Card variant="new" plain>
                <div className={css.cardTitleContainer}>
                  <div className={css.iconRoundContainer}>
                    <IconCoverflex width={8} className={css.circleIcon} />
                  </div>
                  <div className={css.cardTitle}>{cardTitle}</div>
                </div>
                <div className={css.cardAmount}>
                  {savingsDetails ? (
                    MoneyUtils.moneyToString(savingsDetails.total)
                  ) : (
                    <InlineLoading size="large" className={css.loadingAmount} />
                  )}
                </div>
              </Card>
            </Flex>
          </Grid.Column>
        </Grid.Root>
        <div className={css.savingsDetails}>
          <div className={css.savingsDetailsTitle}>
            {t('dashboard.savings.savings_per_benefit.title')}
          </div>
          <div className={css.savingsDetailsBenefits}>
            {savingsDetails && savingsDetails?.benefits.length > 0 ? (
              <Accordion
                savings={savingsDetails.benefits}
                openLearningCentre={() => setShowLearningCentre(true)}
                pristine={pristine}
                setPristine={setPristine}
              />
            ) : (
              <PlaceholderCard
                title={
                  isLoading
                    ? t('dashboard.savings.placeholder.title-loading')
                    : savingsPlaceholderTitle
                }
                text={
                  isLoading
                    ? ''
                    : t('dashboard.savings.placeholder.text-no-savings')
                }
              />
            )}
          </div>
        </div>
      </NestedRoute>
    </div>
  );
};
